import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { adminAPI } from '../../../../services/api';

const IMAGE_MAX_BYTES = 8 * 1024 * 1024;

const TheoryBlockEditor = ({ block, onUpdate }) => {
    const quillRef = useRef(null);
    const onUpdateRef = useRef(onUpdate);
    const [content, setContent] = useState(block.content || '');
    const [uploadError, setUploadError] = useState('');

    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    useEffect(() => {
        const nextContent = block.content || '';
        setContent((currentContent) => (
            currentContent === nextContent ? currentContent : nextContent
        ));
    }, [block.id, block.content]);

    const updateContent = useCallback((value) => {
        setContent(value);
        onUpdateRef.current({ content: value });
    }, []);

    const handleContentChange = (value) => {
        updateContent(value);
    };

    const handleImageUpload = useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;

            if (!file.type.startsWith('image/')) {
                setUploadError('Выберите файл изображения');
                return;
            }
            if (file.size > IMAGE_MAX_BYTES) {
                setUploadError('Размер изображения не должен превышать 8 МБ');
                return;
            }

            const quill = quillRef.current?.getEditor();
            if (!quill) return;

            const range = quill.getSelection(true);
            setUploadError('');

            try {
                const formData = new FormData();
                formData.append('image', file);
                const response = await adminAPI.uploadTextImage(formData);
                const imageUrl = response.data.imageUrl;
                quill.insertEmbed(range.index, 'image', imageUrl);
                quill.setSelection(range.index + 1);
                const html = quill.root.innerHTML;
                updateContent(html);
            } catch (err) {
                const msg =
                    err.response?.data?.message ||
                    err.response?.data?.error ||
                    'Не удалось загрузить изображение';
                setUploadError(msg);
            }
        };
        input.click();
    }, [updateContent]);

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'font': [] }],
                [{ 'size': ['small', false, 'large', 'huge'] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'script': 'sub' }, { 'script': 'super' }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }],
                [{ 'align': [] }],
                ['blockquote', 'code-block'],
                ['link', 'image', 'video'],
                ['clean']
            ],
            handlers: {
                image: handleImageUpload
            }
        }
    }), [handleImageUpload]);

    const formats = [
        'header', 'font', 'size',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'script',
        'list', 'bullet',
        'indent',
        'align',
        'blockquote', 'code-block',
        'link', 'image', 'video'
    ];

    return (
        <div style={{ display: 'grid', gap: '20px' }}>
            <div className="theory-editor-section">
                <label className="form-label" style={{ marginBottom: '10px', display: 'block' }}>
                    Содержимое теории
                </label>
                <div className="quill-container" style={{
                    background: 'white',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    minHeight: '260px',
                    overflow: 'hidden'
                }}>
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={content}
                        onChange={handleContentChange}
                        modules={modules}
                        formats={formats}
                        placeholder="Введите содержимое теории с форматированием..."
                    />
                </div>
                {uploadError && (
                    <p style={{ color: '#d32f2f', fontSize: '13px', marginTop: '8px' }}>{uploadError}</p>
                )}
            </div>

            {content && (
                <div style={{ marginTop: '20px' }}>
                    <label className="form-label" style={{ marginBottom: '10px', display: 'block' }}>
                        Предварительный просмотр
                    </label>
                    <div
                        className="theory-preview"
                        style={{
                            padding: '20px',
                            background: '#f9f9f9',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0',
                            minHeight: '100px'
                        }}
                        dangerouslySetInnerHTML={{ __html: content }}
                    />
                </div>
            )}

            <div style={{
                padding: '15px',
                background: '#e8f4fd',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#1976d2'
            }}>
                <strong>💡 Подсказка:</strong> Изображения добавляйте кнопкой «Изображение» в панели (до 8 МБ) —
                файл сохранится на сервер. Не вставляйте картинки из буфера обмена: они увеличивают размер урока.
            </div>
        </div>
    );
};

export default TheoryBlockEditor;